export const NETWORK_MERGE_VAR_DEFAULTS = {
  dce_name: '',
  dce_address1: '',
  dce_phone: '',
  dce_url: '',
  dce_logo: '',
  dce_color: '',
};

export type NetworkMergeVarValues = typeof NETWORK_MERGE_VAR_DEFAULTS;
export type EditableNetworkMergeVar = keyof NetworkMergeVarValues;
export const EDITABLE_NETWORK_MERGE_VARS = Object.keys(
  NETWORK_MERGE_VAR_DEFAULTS
) as EditableNetworkMergeVar[];

export type NetworkMergeVarDraft = NetworkMergeVarValues;
export type NetworkMergeVarChanges = Partial<
  Record<EditableNetworkMergeVar, string | null>
>;

export interface NetworkChangeRequestLike {
  proposed_changes?: unknown;
}

export interface NetworkMergeVarSource {
  network_merge_vars?: unknown;
}

export interface NetworkWithChangeRequests<
  TRequest extends NetworkChangeRequestLike = NetworkChangeRequestLike
> extends NetworkMergeVarSource {
  change_requests: TRequest[];
}

const hasOwn = (value: object, field: PropertyKey) =>
  Object.prototype.hasOwnProperty.call(value, field);

const pickFields = <T extends object>(
  source: T,
  fields: readonly (keyof T)[]
) =>
  Object.fromEntries(fields.map((field) => [field, source[field]])) as Pick<
    T,
    typeof fields[number]
  >;

const pickPresentFields = <T extends object>(
  source: Partial<T>,
  fields: readonly (keyof T)[]
) =>
  Object.fromEntries(
    fields
      .filter((field) => hasOwn(source, field))
      .map((field) => [field, source[field]])
  ) as Partial<T>;

export const getPendingNetworkRequest = <
  TRequest extends NetworkChangeRequestLike
>(
  network: NetworkWithChangeRequests<TRequest>
) => network.change_requests[0];

export const getLiveNetworkMergeVarValues = (
  network: NetworkMergeVarSource
): NetworkMergeVarValues => {
  const mergeVars = (network.network_merge_vars ||
    {}) as Partial<NetworkMergeVarValues>;
  return (Object.fromEntries(
    EDITABLE_NETWORK_MERGE_VARS.map((field) => [
      field,
      mergeVars[field] ?? NETWORK_MERGE_VAR_DEFAULTS[field],
    ])
  ) as unknown) as NetworkMergeVarValues;
};

export const requestToNetworkMergeVarChanges = (
  request?: NetworkChangeRequestLike
): NetworkMergeVarChanges => {
  const proposedChanges = (request?.proposed_changes || {}) as {
    network_merge_vars?: NetworkMergeVarChanges;
  };
  return pickPresentFields(
    proposedChanges.network_merge_vars || {},
    EDITABLE_NETWORK_MERGE_VARS
  );
};

export const getNetworkMergeVarBaseline = (
  network: NetworkWithChangeRequests
): NetworkMergeVarDraft => {
  const live = getLiveNetworkMergeVarValues(network);
  const proposed = requestToNetworkMergeVarChanges(
    getPendingNetworkRequest(network)
  );
  return (Object.fromEntries(
    EDITABLE_NETWORK_MERGE_VARS.map((field) => [
      field,
      hasOwn(proposed, field) ? proposed[field] ?? '' : live[field],
    ])
  ) as unknown) as NetworkMergeVarDraft;
};

export const draftToNetworkMergeVarChanges = (
  network: NetworkMergeVarSource,
  draft: NetworkMergeVarDraft
): NetworkMergeVarChanges => {
  const live = getLiveNetworkMergeVarValues(network);
  const changedFields = EDITABLE_NETWORK_MERGE_VARS.filter(
    (field) => draft[field] !== live[field]
  );
  return Object.fromEntries(
    changedFields.map((field) => [field, draft[field] || null])
  ) as NetworkMergeVarChanges;
};

export const originalValuesForNetworkMergeVarChanges = (
  network: NetworkMergeVarSource,
  proposed: NetworkMergeVarChanges
): NetworkMergeVarChanges => {
  const live = getLiveNetworkMergeVarValues(network);
  return pickFields(
    live,
    EDITABLE_NETWORK_MERGE_VARS.filter((field) => hasOwn(proposed, field))
  );
};

export const networkMergeVarDraftsEqual = (
  a: NetworkMergeVarDraft,
  b: NetworkMergeVarDraft
) => EDITABLE_NETWORK_MERGE_VARS.every((field) => a[field] === b[field]);

export const networkMergeVarChangesEqual = (
  a: NetworkMergeVarChanges,
  b: NetworkMergeVarChanges
) =>
  EDITABLE_NETWORK_MERGE_VARS.every(
    (field) => hasOwn(a, field) === hasOwn(b, field) && a[field] === b[field]
  );
